import React, {ChangeEvent, useState} from 'react';
import {FieldProp, Form} from '../../types';
import {Flow, FlowFetchResponse} from '../../_services/Flow';
import {baseName, fieldValueLabel, parseMergeTag} from '../../_helpers/form';
import {FormState} from '../Form';

type FileUploadProps = FieldProp & {};

/*
const humanFileSize = (size: number): string => {
    const i: number = Math.floor(Math.log(size) / Math.log(1024));
    // @ts-ignore
    return (size / Math.pow(1024, i)).toFixed(2) * 1 + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i];
};
 */

export const FileUpload: React.FunctionComponent<FileUploadProps> = ({
                                                                         field,
                                                                         fieldState,
                                                                         onChange,
                                                                         value,
                                                                         displayOnly,
                                                                         setIsBusy,
                                                                         formState,
                                                                         form
                                                                     }): JSX.Element => {
    const [fileIsUploading, setFileIsUploading] = useState<boolean>(false);
    const [fileUploadError, setFileUploadError] = useState<string>('');

    // File Input is not a controlled component, in order to reset state on error we are
    // assigning a key to the input component. Changing the key value forces react to
    // re-render the component, effectively "clearing" it
    const [fileInputKey, setFileInputKey] = useState<number>(1);

    const {
        touched,
        pristine,
        validating,
        valid,
        invalid,
        errors,
    } = fieldState;
    const classNames = [
        /* 'form-control', */
        touched && 'is-touched',
        pristine && 'is-pristine',
        validating && 'is-validating',
        valid && 'is-valid',
        invalid && 'is-invalid',
        field.chainedSelectsAlignment === 'horizontal' && 'col',
        field.chainedSelectsAlignment === 'vertical' && 'col-12',
    ]
        .filter(Boolean)
        .join(' ');

    const handleFileUpload = (file: File) => {
        setFileIsUploading(true);
        setFileUploadError('');
        setIsBusy && setIsBusy(true);

        Flow.getInstance().upload(field.formId, file)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    onChange && onChange(response.json.file);
                } else {
                    setFileUploadError(response.json.message || 'Error uploading file');
                    setFileInputKey(fileInputKey + 1);
                }
                setFileIsUploading(false);
                setIsBusy && setIsBusy(false);
            })
            .catch((error: any) => {
                setFileIsUploading(false);
                setFileUploadError(error || 'Error uploading file');
                setFileInputKey(fileInputKey + 1);
            });
    };

    // if passed a custom filename, use that as the value of <a> as well as the query string parameter filename
    let filename = baseName(value);
    const customFilename = (field?.flowCustomDisplayComponentConfig?.hasOwnProperty('customFilename')) ? field.flowCustomDisplayComponentConfig.customFilename : false;
    if (customFilename) {
        filename = customFilename.replace(/{.*?}/g, (match: any) => {
            return parseMergeTag(match, form as Form, formState as FormState)
        });
    }

    const getFieldValueLabel = () => (
        <a href={`/p/flow/api/download-file?url=${value}&filename=${filename}`} target="_blank">
            {filename}
        </a>
    );

    return (
        <div>
            {(displayOnly || value) ?
                value && <div className="d-flex align-items-center">
                    <p className="m-0 p-0">
                        {getFieldValueLabel()}
                        {/* fieldValueLabel(formState || {fieldValues: {[field.name]: value}} as FormState, field) */}
                    </p>
                    {!displayOnly &&
                        <button type="button"
                                className="btn ml-1"
                                aria-label="Close"
                                onClick={() => {
                                    onChange && onChange('');
                                    setFileInputKey(fileInputKey + 1);
                                }}
                        >
                            <i className="fas fa-times text-danger"/>
                        </button>
                    }
                </div>
                :
                <div>
                    <input
                        key={fileInputKey}
                        type="file"
                        className={classNames}
                        disabled={fileIsUploading}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            e.target &&
                            e.target.files &&
                            e.target.files.length &&
                            handleFileUpload(e.target.files[0]);
                        }}
                    />
                    {fileIsUploading && <i className="fas fa-spin fa-circle-notch"/>}
                    {fileUploadError &&
                        <div className="invalid-feedback">
                            {fileUploadError}
                        </div>
                    }
                    {errors && errors.map((error: string, index: number) => (
                        <div key={index} className="col-12 invalid-feedback">
                            {error}
                        </div>
                    ))}
                </div>
            }

        </div>
    );
};